/**
 * Customer x job-type prices in Excel (gap 1 PR 2, DEC-383, D-12).
 *
 * "Download Excel" / "Download CSV" give one row per customer x job type the customer may use,
 * with today's price and where it comes from. The "New Price ($)" column is blank: type a price
 * there to add or change it, and leave it blank to change nothing. "Special Price ($)" shows what
 * is set today and is never read back.
 *
 * Editors (canEdit, UX only) also get "Import a file": the file is read on the server (bulk/parse),
 * then ONE start date (today or later) applies to every price in it, then a before/after preview,
 * then "Save N changes" saves exactly what was previewed (usePreviewApply). The API is the gate
 * (VIEW_INVOICES to download, MANAGE_CUSTOMER_PRICES to import).
 *
 * The file is for billing staff; it is not a customer document.
 */

import React from 'react';
import { Button } from 'src/components/ui/button';
import { PriceChangePreview, type PreviewColumn } from './PriceChangePreview';
import { StartDateInput } from './StartDateInput';
import { usePreviewApply } from './use-preview-apply';
import {
  priceText,
  type JobTypeBulkApplyResponse,
  type JobTypeBulkRequest,
  type JobTypePreviewRow,
} from './customer-prices';
import {
  BULK_APPLY_PATH,
  BULK_PREVIEW_PATH,
  CUSTOMER_PRICES_KEY,
  useDownloadJobTypePrices,
  useParseJobTypePriceFile,
} from './customer-prices-hooks';

const helpStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };
const errorStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--color-danger)' };

type Row = JobTypePreviewRow;

function cents(row: Row, value: number | null): string {
  if (value === null) return 'No price';
  return priceText(value, row.jobType?.billingMethod ?? '');
}

const COLUMNS: readonly PreviewColumn<Row>[] = [
  { key: 'line', header: 'Line', cell: (r) => (r.line === null ? '—' : String(r.line)) },
  {
    key: 'customer',
    header: 'Customer',
    cell: (r) => r.customer?.name ?? r.customer?.abbreviation ?? '—',
  },
  { key: 'jobType', header: 'Job type', cell: (r) => r.jobType?.label ?? '—' },
  { key: 'now', header: 'Now', cell: (r) => cents(r, r.currentCents) },
  { key: 'new', header: 'New', cell: (r) => (r.newCents === null ? '—' : cents(r, r.newCents)) },
];

function savedText(n: number): string {
  return `Saved ${String(n)} ${n === 1 ? 'price' : 'prices'}.`;
}

export function JobTypePricesExcel(props: {
  today: string;
  customerId?: string;
  canEdit: boolean;
}): React.JSX.Element {
  const download = useDownloadJobTypePrices();
  const parse = useParseJobTypePriceFile();
  const bulk = usePreviewApply<JobTypeBulkRequest, Row, JobTypeBulkApplyResponse>({
    previewPath: BULK_PREVIEW_PATH,
    applyPath: BULK_APPLY_PATH,
    invalidate: [CUSTOMER_PRICES_KEY],
  });
  const inputId = React.useId();
  const [fileName, setFileName] = React.useState<string | null>(null);
  const [fileRows, setFileRows] = React.useState<string[][] | null>(null);
  const [effectiveFrom, setEffectiveFrom] = React.useState(props.today);

  function resetPreview(): void {
    bulk.reset();
  }

  function onFile(file: File | undefined): void {
    setFileRows(null);
    setFileName(file?.name ?? null);
    resetPreview();
    parse.reset();
    if (file === undefined) return;
    parse.mutate(file, { onSuccess: (r) => setFileRows(r.rows) });
  }

  function runPreview(): void {
    if (fileRows === null) return;
    void bulk.preview({ kind: 'IMPORT', rows: fileRows, effectiveFrom });
  }

  const rows = bulk.rows;
  const toSave =
    rows === null ? 0 : rows.filter((r) => r.status === 'ADD' || r.status === 'CHANGE').length;
  const busy = parse.isPending || bulk.status === 'previewing' || bulk.status === 'applying';
  const early = effectiveFrom === '' || effectiveFrom < props.today;

  return (
    <div style={{ display: 'grid', gap: '12px', maxWidth: '900px' }}>
      <p style={helpStyle}>
        Type a price in the New Price column to add or change it. Leave it blank to change nothing.
      </p>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <Button
          type="button"
          variant="outline"
          disabled={download.isPending}
          onClick={(): void =>
            download.mutate({
              format: 'xlsx',
              ...(props.customerId !== undefined ? { customerId: props.customerId } : {}),
            })
          }
        >
          Download Excel
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={download.isPending}
          onClick={(): void =>
            download.mutate({
              format: 'csv',
              ...(props.customerId !== undefined ? { customerId: props.customerId } : {}),
            })
          }
        >
          Download CSV
        </Button>
      </div>
      {download.isError && (
        <p role="alert" style={errorStyle}>
          {download.error.message}
        </p>
      )}

      {props.canEdit && (
        <>
          <div>
            <label
              htmlFor={inputId}
              style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}
            >
              Import a file
            </label>
            <input
              id={inputId}
              type="file"
              accept=".xlsx,.csv"
              disabled={busy}
              onChange={(e): void => {
                onFile(e.target.files?.[0]);
                // The same file can be chosen again after a fix.
                e.target.value = '';
              }}
            />
          </div>
          {parse.isPending && (
            <p role="status" style={helpStyle}>
              Reading the file…
            </p>
          )}
          {parse.isError && (
            <p role="alert" style={errorStyle}>
              {parse.error.message}
            </p>
          )}

          {fileRows !== null && (
            <>
              <p
                style={helpStyle}
              >{`Read ${fileName ?? 'the file'}. Pick the start date, then preview.`}</p>
              <div>
                <StartDateInput
                  value={effectiveFrom}
                  today={props.today}
                  onChange={(v): void => {
                    setEffectiveFrom(v);
                    resetPreview();
                  }}
                />
                <p style={{ ...helpStyle, marginTop: '4px' }}>
                  One start date for every price in the file
                </p>
              </div>
              <div>
                <Button type="button" disabled={busy || early} onClick={runPreview}>
                  Preview
                </Button>
              </div>
            </>
          )}

          {bulk.status === 'previewing' && (
            <p role="status" style={helpStyle}>
              Working out the new prices…
            </p>
          )}
          {bulk.status === 'error' && bulk.error !== null && (
            <p role="alert" style={errorStyle}>
              {bulk.error}
            </p>
          )}
          {bulk.status === 'stale' && (
            <div role="alert" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={errorStyle}>{bulk.error}</span>
              <Button type="button" size="sm" variant="outline" onClick={runPreview}>
                Preview again
              </Button>
            </div>
          )}
          {bulk.status === 'done' && bulk.result !== null && (
            <p role="status" style={{ color: 'var(--color-success)' }}>
              {savedText(bulk.result.applied.add + bulk.result.applied.change)}
            </p>
          )}

          {rows !== null && bulk.status !== 'done' && (
            <>
              {rows.length === 0 ? (
                <p style={helpStyle}>The file has no rows.</p>
              ) : (
                <PriceChangePreview rows={rows} columns={COLUMNS} />
              )}
              <div>
                <Button
                  type="button"
                  disabled={!bulk.canApply || toSave === 0}
                  onClick={(): void => {
                    void bulk.apply();
                  }}
                >
                  {`Save ${String(toSave)} ${toSave === 1 ? 'change' : 'changes'}`}
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
